import type { DefectFeaturesResponseBody } from '../contracts/defectFeatures';
import type { DefectRule } from '../contracts/defectRulesets';
import { buildDefectRulesRenderer } from './buildDefectRulesRenderer';
import {
  inferGeometryKindFromFeatures,
  toArcGisGeometryType,
  type DefectGeometryKind,
} from './defectGeometry';
import { createGeoJsonLayerUrl } from './geoJsonBlobUrl';
import { LEGEND_SURVEY_LAYER_SPATIAL_REFERENCE } from './legendSurveySectionGeometry';

/* GeoJSONLayer in @arcgis/core 4.x loads client data via `url` (blob URLs included), not `source`. */
export interface DefectGeoJsonLayerConfig {
  readonly blobUrl: string;
  readonly geometryKind: DefectGeometryKind;
  readonly layerProperties: {
    readonly id: string;
    readonly url: string;
    readonly title: string;
    readonly geometryType: ReturnType<typeof toArcGisGeometryType>;
    readonly popupEnabled: false;
    readonly spatialReference: typeof LEGEND_SURVEY_LAYER_SPATIAL_REFERENCE;
    readonly renderer: ReturnType<typeof buildDefectRulesRenderer>;
    readonly outFields: readonly ['*'];
  };
}

export function createDefectGeoJsonLayerConfig(
  mapLayerId: string,
  title: string,
  collection: DefectFeaturesResponseBody,
  rules: readonly DefectRule[],
  lineWidth?: number,
  lineStyle?: string,
  geometryKind: DefectGeometryKind = inferGeometryKindFromFeatures(collection),
): DefectGeoJsonLayerConfig {
  const blobUrl = createGeoJsonLayerUrl(collection);

  return {
    blobUrl,
    geometryKind,
    layerProperties: {
      id: mapLayerId,
      url: blobUrl,
      title,
      geometryType: toArcGisGeometryType(geometryKind),
      popupEnabled: false,
      spatialReference: LEGEND_SURVEY_LAYER_SPATIAL_REFERENCE,
      renderer: buildDefectRulesRenderer(rules, lineWidth, lineStyle, geometryKind),
      outFields: ['*'],
    },
  };
}
